import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useIsFirstRender, usePathname, useUI} from 'coreHooks';

import {IScrollRestoration} from './IScrollRestoration';
import {ScrollRestorationProvider} from './ScrollRestorationProvider';

const SCROLL_HEIGHT_KEY_SUFFIX = '.scrollHeight';

const ScrollRestoration = ({children, triggerScroll, triggerChange, isPageKeptAlive = false}: IScrollRestoration) => {
	const pathname = usePathname();
	const scrollAfterLoading = useUI('scrollAfterLoading');
	const poppedRef = useRef(false);
	const locationKeyRef = useRef(null);
	const scrollTimeoutRef = useRef(null);
	const onRestoration = useRef<(() => void) | null>(null);
	const isFirstRender = useIsFirstRender();
	const [localTriggerScroll, setLocalTriggerScroll] = useState(triggerScroll);

	const getScrollHeightKey = useCallback(
		() => locationKeyRef.current + SCROLL_HEIGHT_KEY_SUFFIX,
		[],
	);

	const popStateListener = useCallback(() => {
		poppedRef.current = true;
	}, []);

	const saveScrollPosition = useCallback(() => {
		scrollTimeoutRef.current = null;
		sessionStorage.setItem(locationKeyRef.current, window.scrollY.toString());
		sessionStorage.setItem(
			getScrollHeightKey(),
			document.documentElement.scrollHeight.toString(),
		);
	}, []);

	const scrollHandler = useCallback(() => {
		clearTimeout(scrollTimeoutRef.current);

		if (poppedRef.current) {
			return;
		}

		scrollTimeoutRef.current = setTimeout(saveScrollPosition, 400);
	}, []);

	useEffect(() => {
		document.addEventListener('scroll', scrollHandler, {passive: true});
		window.addEventListener('popstate', popStateListener, {passive: true});
		window.history.scrollRestoration = 'manual';

		return () => {
			document.removeEventListener('scroll', scrollHandler);
			window.removeEventListener('popstate', popStateListener);
			window.history.scrollRestoration = 'auto';
		};
	}, []);

	useEffect(() => {
		// Last scroll of the page being left, not saved yet (link clicked right after scrolling): saved now,
		// while the page is still on screen (a page kept alive is restored at this position too).
		if (scrollTimeoutRef.current && locationKeyRef.current) {
			clearTimeout(scrollTimeoutRef.current);
			saveScrollPosition();
		}

		locationKeyRef.current = `${pathname}.lastPos`;
	}, [pathname]);

	useEffect(() => {
		setLocalTriggerScroll(triggerScroll);
	}, [triggerScroll]);

	useEffect(() => {
		if (isFirstRender) {
			return;
		}

		if (isPageKeptAlive) {
			poppedRef.current = false;
		} else if (poppedRef.current) {
			const savedScrollPosition = sessionStorage.getItem(locationKeyRef.current);

			if (savedScrollPosition) {
				const intSavedScrollPosition = parseInt(savedScrollPosition);

				if (intSavedScrollPosition <= document.documentElement.scrollHeight) {
					ViewportMgr.scrollTo(intSavedScrollPosition);
					onRestoration?.current?.();
				}
			} else {
				ViewportMgr.scrollTo(0);
			}

			poppedRef.current = false;
		} else if (scrollAfterLoading) {
			ViewportMgr.scrollTo(0);
		}
	}, [localTriggerScroll]);

	const contextValue = useMemo(
		() => {
			return {
				clearCurrentLocation: () => {
					sessionStorage.removeItem(locationKeyRef.current);
					sessionStorage.removeItem(locationKeyRef.current + SCROLL_HEIGHT_KEY_SUFFIX);
				},
				setOnRestoration: (cb) => {
					onRestoration.current = cb;
				},
				getSavedScrollHeight: () => {
					return parseInt(
						sessionStorage.getItem(locationKeyRef.current + SCROLL_HEIGHT_KEY_SUFFIX) || '0',
						10,
					);
				},
				routeChanged: triggerChange,
			};
		},
		[triggerChange],
	);

	return (
		<ScrollRestorationProvider value={contextValue}>
			{children}
		</ScrollRestorationProvider>
	);
};

export default ScrollRestoration;
